import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import { cn } from '../../utils';

const AutoResizingTextarea = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  minHeight = 40,
  maxHeight = 200,
  disabled = false,
}) => {
  const textareaRef = useRef(null);

  // Auto-resize the textarea based on content
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate the new height
    const newHeight = Math.min(
      Math.max(textarea.scrollHeight, minHeight),
      maxHeight,
    );

    textarea.style.height = `${newHeight}px`;
  }, [minHeight, maxHeight]);

  // Adjust height when value changes
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  return (
    <div className="relative w-full">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder || 'Message...'}
        rows={1}
        disabled={disabled}
        className={cn(
          'w-full outline-none resize-none overflow-y-auto leading-5',
          'transition-all duration-200 ease-in-out',
          {
            'opacity-50 cursor-not-allowed': disabled,
          },
        )}
        style={{
          minHeight: `${minHeight}px`,
          maxHeight: `${maxHeight}px`,
        }}
      />
    </div>
  );
};

AutoResizingTextarea.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  disabled: PropTypes.bool,
};

export default AutoResizingTextarea;
